import React from 'react';
import { ReactComponent as ArrowDownFillIcon } from '../../../assets/icons/arrow-down-s-fill.svg';
import { ReactComponent as ArrowUpFillIcon } from '../../../assets/icons/arrow-up-s-fill.svg';
import { ReactComponent as ArrowDownUnfillIcon } from '../../../assets/icons/arrow-down-s-unfill.svg';
import '../../../Components/Common/typography.css';
import { formatLocaleNumber, formatYears } from '../../../helpers/number';
import { formatDirectorName } from '../../../helpers/string';
import { CompanyDirector } from '../../../types/companydirector.type';
import { CommonTooltipType } from '../../../types/common.type';
import { DirectorOrderBy, DirectorSortBy } from '../../../utils/type';

interface TableBasicProps {
  directors: CompanyDirector[];
  sortBy: DirectorSortBy;
  orderBy: DirectorOrderBy;
  onSortChange: (sortBy: DirectorSortBy, orderBy: DirectorOrderBy) => void;
  currency: string | null;
  setTooltip: React.Dispatch<React.SetStateAction<CommonTooltipType>>;
}

const TableBasic: React.FC<TableBasicProps> = ({
  directors,
  sortBy,
  orderBy,
  onSortChange,
  currency,
  setTooltip,
}) => {
  const handleTitleMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    content: string,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      content,
      show: true,
      x: rect.left + window.scrollX,
      y: rect.bottom + 2 + window.scrollY,
    });
  };
  const handleTitleMouseLeave = () =>
    setTooltip((prev) => ({ ...prev, show: false }));

  const handleSort = (col: DirectorSortBy) => {
    onSortChange(
      col,
      sortBy === col && orderBy === DirectorOrderBy.Asc
        ? DirectorOrderBy.Desc
        : DirectorOrderBy.Asc,
    );
  };

  const sortedStyle = (col: DirectorSortBy) =>
    sortBy === col ? { paddingRight: '12px' } : undefined;

  const SortIcon = ({ col }: { col: DirectorSortBy }) => {
    const isActive = sortBy === col;
    const Icon = isActive
      ? orderBy === DirectorOrderBy.Asc
        ? ArrowUpFillIcon
        : ArrowDownFillIcon
      : ArrowDownUnfillIcon;

    return (
      <Icon
        className={`director-sort-icon${
          isActive ? ' director-sort-icon--fill' : ''
        }`}
      />
    );
  };

  const thProps = (col: DirectorSortBy) => ({
    onClick: () => handleSort(col),
    role: 'button' as const,
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => e.key === 'Enter' && handleSort(col),
  });

  return (
    <div>
      <div className="director-basic-header">
        <div className="director-th header-6" {...thProps(DirectorSortBy.LastName)}>
          <span>Current officer</span>
          <SortIcon col={DirectorSortBy.LastName} />
        </div>
        <div className="director-th header-6" {...thProps(DirectorSortBy.Title)}>
          <span>Current position</span>
          <SortIcon col={DirectorSortBy.Title} />
        </div>
        <div
          className="director-th header-6 director-th--right"
          onClick={() => handleSort(DirectorSortBy.OldPosition)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === 'Enter' && handleSort(DirectorSortBy.OldPosition)
          }
        >
          <div className="director-th-text-stack">
            <span>Officer/director</span>
            <span className="body-s-regular">(YRS)</span>
          </div>
          <SortIcon col={DirectorSortBy.OldPosition} />
        </div>
        <div
          className="director-th header-6 director-th--right"
          onClick={() => handleSort(DirectorSortBy.LatestPosition)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === 'Enter' && handleSort(DirectorSortBy.LatestPosition)
          }
        >
          <div className="director-th-text-stack">
            <span>In position</span>
            <span className="body-s-regular">(YRS)</span>
          </div>
          <SortIcon col={DirectorSortBy.LatestPosition} />
        </div>
        <div
          className="director-th header-6 director-th--right"
          onClick={() => handleSort(DirectorSortBy.Compensation)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) =>
            e.key === 'Enter' && handleSort(DirectorSortBy.Compensation)
          }
        >
          <div className="director-th-text-stack">
            <span>Total compensation</span>
            <span className="body-s-regular">
              {currency ? `(${currency})` : ''}
            </span>
          </div>
          <SortIcon col={DirectorSortBy.Compensation} />
        </div>
      </div>

      {directors.map((d) => (
        <div key={d.Officer_ID} className="director-basic-row">
          <div className="director-td director-td--top">
            <div className="director-name-group">
              <span className="director-name-text body-m-regular">
                {formatDirectorName(
                  d.NamePrefix,
                  d.FirstName,
                  d.MiddleName,
                  d.LastName,
                )}
              </span>
              <span
                className="director-position-text body-s-regular"
                onMouseEnter={(e) =>
                  handleTitleMouseEnter(e, d.Latest_Long_Title)
                }
                onMouseLeave={handleTitleMouseLeave}
              >
                {d.Latest_Long_Title}
              </span>
            </div>
          </div>
          <div
            className="director-td director-td--right director-td--top body-m-regular"
            style={sortedStyle(DirectorSortBy.OldPosition)}
          >
            {formatYears(d.Oldest_position_year)}
          </div>
          <div
            className="director-td director-td--right director-td--top body-m-regular"
            style={sortedStyle(DirectorSortBy.LatestPosition)}
          >
            {formatYears(d.Latest_position_year)}
          </div>
          <div
            className="director-td director-td--right director-td--top body-m-regular"
            style={sortedStyle(DirectorSortBy.Compensation)}
          >
            {formatLocaleNumber(d.Latest_FYT_val)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TableBasic;
